<template>
  <div class="flex flex-column gap-3">
    <div class="font-semibold font-title">Repeat</div>
    <div class="flex flex-column gap-3">
      <div v-if="!isCustom && selectedPreset">
        {{ selectedPreset.label }} {{ selectedPreset.subtitle(startDate) }}
      </div>
      <div v-if="isCustom" class="flex gap-3 items-center">
        <span class="text-titles">Every</span>
        <NumberInput
          v-model="recurrenceRule.interval"
          :min="1"
          :disabled="!isCustom"
          @update:modelValue="handleIntervalChange"
        />
        <Select
          v-model="recurrenceRule.type"
          class="interval-select"
          :options="decoratedRuleTypeOptions"
          :allow-empty="false"
          :disabled="!isCustom"
          @update:modelValue="handleRuleTypeChange"
        ></Select>
      </div>
      <div v-if="isCustom && isWeekly" class="flex gap-3 items-center">
        <span class="text-titles">On</span>
        <DaysPicker v-model="recurrenceRule.days" :disabled="!isCustom" />
      </div>
      <div v-if="isCustom && isMonthly" class="flex gap-3 items-center">
        <span class="text-titles">On</span>
        <ButtonToggle
          v-model="monthOption"
          :options="monthOptions"
          :disabled="!isCustom"
          @update:modelValue="handleMonthOptionChange"
        />
      </div>
      <div class="flex flex-column gap-3">
        <span class="text-titles">Ends</span>
        <RadioButton
          v-model="ends"
          radio-value="never"
          label="Never"
          class="m-0"
        />
        <RadioButton v-model="ends" radio-value="on" label="On" class="m-0">
          <Datepicker
            v-model="until"
            class="until-input"
            :field-error-message="firstErrorMessage(v$.endsOn)"
          />
        </RadioButton>
        <RadioButton
          v-model="ends"
          radio-value="after"
          label="After"
          class="m-0"
        >
          <div class="flex items-center gap-3">
            <NumberInput v-model="count" :min="1" />
            {{ inflect("Occurrence", recurrenceRule.count) }}
          </div>
        </RadioButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inflect } from "inflection";
import moment, { Moment } from "moment";
import { unitOfTime } from "moment-timezone";
import NumberInput from "serviceshift-ui/components/Inputs/NumberInput.vue";
import { computed, ref, watch } from "vue";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import ButtonToggle from "@/components/ButtonToggle.vue";
import Datepicker from "@/components/Datepicker.vue";
import RadioButton from "@/components/RadioButton.vue";
import { addOrdinalSuffix } from "@/mixins";
import DaysPicker from "@/Scheduler/CalendarEventModal/forms/DaysPicker.vue";
import {
  RecurrencePresetName,
  RecurrenceRule,
  RecurrenceRuleType,
  Weekday
} from "@/typings/calendarEvent";
import {
  currentWeekday,
  getRecurrencePreset,
  recurrencePresets,
  recurrenceTypeOptions
} from "@/typings/calendarEventForm";
import { Validation } from "@vuelidate/core";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: Object as () => RecurrenceRule,
    required: true
  },
  presetName: {
    type: String as () => RecurrencePresetName,
    required: true
  },
  startDate: {
    type: Object as () => Moment,
    required: true
  },
  v$: {
    type: Object as () => Validation,
    required: true
  }
});

const recurrenceRule = ref(props.modelValue);
watch(recurrenceRule, (newValue) => {
  until.value = newValue.until;
  count.value = newValue.count;
});

const selectedPreset = computed(() => getRecurrencePreset(props.presetName));

const startDate = ref(props.startDate);
watch(
  () => props.startDate,
  (newValue) => {
    startDate.value = newValue;

    updateEndsComputation();
  }
);

const monthOptions = computed(() => {
  // compute what occurrence this weekday is, e.g. 1st Friday
  let occurrence = 0;

  const refDate = moment(startDate.value);

  while (refDate.month() === startDate.value.month()) {
    occurrence++;
    refDate.subtract(1, "week");
  }

  return [
    {
      label: "the " + startDate.value.format("Do"),
      value: startDate.value.format("D") + "OM"
    },
    {
      label: `the ${addOrdinalSuffix(occurrence)} ${startDate.value.format(
        "ddd"
      )}`,
      value: occurrence + ":" + startDate.value.format("d")
    }
  ];
});

const computedMonthOption = computed(() => {
  if (props.modelValue.occurrence) {
    return monthOptions.value.at(1)!.value;
  } else {
    return monthOptions.value.at(0)!.value;
  }
});

const monthOption = ref(computedMonthOption.value);

const isCustom = computed(() => {
  return props.presetName === "custom";
});
const isMonthly = computed(() => {
  return recurrenceRule.value.type === "MonthlyRule";
});
const isWeekly = computed(() => {
  return recurrenceRule.value.type === "WeeklyRule";
});

const ends = ref(props.modelValue.ends);
watch(ends, (newValue) => {
  recurrenceRule.value.ends = newValue;
});

const until = ref(props.modelValue.until);
watch(until, (newValue) => {
  recurrenceRule.value.until = newValue;
});

const count = ref(props.modelValue.count);
watch(count, (newValue) => {
  recurrenceRule.value.count = newValue;
});

watch(
  () => props.presetName,
  (newValue: RecurrencePresetName) => {
    recurrenceRule.value = recurrencePresets
      .find((preset) => preset.name === newValue)!
      .toRecurrenceRule();

    emit("update:modelValue", recurrenceRule.value);
  }
);

watch(
  recurrenceRule,
  (newValue) => {
    emit("update:modelValue", newValue);
  },
  { deep: true }
);

function handleIntervalChange(_newValue: number, oldValue: number) {
  if (!oldValue) {
    return;
  }
  updateEndsComputation();
}

function handleRuleTypeChange(
  newValue: RecurrenceRuleType,
  oldValue: RecurrenceRuleType
) {
  const hydrating = !oldValue;

  if (hydrating) {
    if (newValue === RecurrenceRuleType.MonthlyRule) {
      handleMonthOptionChange(monthOption.value);
    }

    updateEndsComputation();

    return;
  }

  switch (newValue) {
    case RecurrenceRuleType.DailyRule:
      recurrenceRule.value.days = [];
      recurrenceRule.value.occurrence = undefined;
      break;
    case RecurrenceRuleType.WeeklyRule:
      recurrenceRule.value.days = [currentWeekday()];
      recurrenceRule.value.occurrence = undefined;
      break;
    case RecurrenceRuleType.MonthlyRule:
      handleMonthOptionChange(monthOption.value);
      break;
    case RecurrenceRuleType.YearlyRule:
      recurrenceRule.value.days = [];
      recurrenceRule.value.occurrence = undefined;
      break;
  }

  updateEndsComputation();
}

// like Google Calendar - when interval/frequency is changed, reset the ends after/on
function updateEndsComputation() {
  let unit: unitOfTime.DurationConstructor = "weeks";

  switch (recurrenceRule.value.type) {
    case RecurrenceRuleType.DailyRule:
      count.value = 30;
      unit = "days";
      break;
    case RecurrenceRuleType.WeeklyRule:
      count.value = 13;
      unit = "weeks";
      break;
    case RecurrenceRuleType.MonthlyRule:
      count.value = 12;
      unit = "months";
      break;
    case RecurrenceRuleType.YearlyRule:
      count.value = 5;
      unit = "years";
      break;
  }

  until.value = moment(startDate.value)
    .add(count.value * recurrenceRule.value.interval!, unit)
    .toDate();
}

function handleMonthOptionChange(newValue: string) {
  if (newValue.endsWith("OM")) {
    // day-of-month
    recurrenceRule.value.days = [
      parseInt(newValue.substring(0, newValue.length - 2))
    ];
    recurrenceRule.value.occurrence = undefined;
  } else {
    // nth-day-of-week, e.g. 2:2 -> (2nd Tuesday)
    const occurrence: number = parseInt(newValue.substring(0, 1));
    const weekday: Weekday = parseInt(newValue.substring(2)) as Weekday;

    recurrenceRule.value.days = [weekday];
    recurrenceRule.value.occurrence = [occurrence];
  }

  emitChanges();
}

const decoratedRuleTypeOptions = computed(() => {
  return recurrenceTypeOptions.map((item) => {
    return {
      ...item,
      label: inflect(item.label, recurrenceRule.value.interval!)
    };
  });
});

function emitChanges() {
  emit("update:modelValue", recurrenceRule.value);
}
</script>

<style scoped lang="scss">
.interval-select {
  width: 100px;
}

.until-input {
  width: 110px !important;
}
</style>
